import type { LoggerDraft } from './loggerDraft';
import {
  hasCommittedWork,
  parsePersistedBlob,
  type PersistedLoggerState,
} from './loggerDraftPersistence';
import {
  blobOwner,
  isOwnDraft,
  loggerDraftOwner,
  loggerDraftOwnerInjected,
} from './draftOwner';
import {
  quarantineSlotsToReap,
  type QuarantineSlot,
} from './quarantineReaper';

// web-rnw twin of ./loggerDraftStorage.ts (see that file's header) — same
// key namespace + JSON shape, localStorage instead of AsyncStorage.

// ⭐ Backend scope (INFRA-1 slice B). The mobile mount injects the active
// stage at boot; production and every web mount leave it empty, so the keys
// stay exactly as they have always been — nothing migrates. A dev package
// pointed at another backend keeps that backend's draft apart, because a
// draft resumed against the wrong API would save real sets to the wrong
// place. The stage is injected, never imported: this file is shared code and
// knows nothing about mobile's backend module.
let scope = '';

export function setLoggerDraftScope(stage: string | null): void {
  scope = stage === null || stage === '' ? '' : `.${stage}`;
}

const liveKey = () => `jl.workoutLog.v1${scope}`;

/** The live slot's key: mobile's boot gives its owner to an unstamped one
 *  (#288, apps/mobile state/draftOwnership.ts). */
export const liveDraftKey = liveKey;

/** Prefix for timestamped quarantine slots. A function, not a const, because
 *  the scope is only known after boot. The stage sits BEFORE `quarantine` so
 *  production's prefix is not also a prefix of another stage's keys — an
 *  enumeration on production must never turn up a lab draft.
 *  Exported so the mobile recovery store
 *  (apps/mobile/src/state/quarantineStore.ts) can enumerate + reload
 *  preserved blobs. */
export const quarantinePrefix = () => `jl.workoutLog${scope}.quarantine.v1.`;

export async function loadPersistedLoggerState(): Promise<PersistedLoggerState | null> {
  try {
    const raw = localStorage.getItem(liveKey());
    if (raw === null) return null;
    const state = parsePersistedBlob(raw);
    // Another account's draft is not this one's to resume or overwrite:
    // the shell sets it aside for its owner at mount (bootDraftVerdict).
    return state !== null && isOwnDraft(state, loggerDraftOwner()) ? state : null;
  } catch {
    return null;
  }
}

/** Same guard as the AsyncStorage twin. Web injects no owner today, so
 *  it never finds a foreign draft; kept so the two cannot drift. */
async function setAsideForeignDraft(owner: string | null): Promise<void> {
  const raw = localStorage.getItem(liveKey());
  if (raw === null) return;
  const other = blobOwner(raw);
  if (other === undefined || other === owner) return;
  if (!(await setAsideLiveDraft())) {
    console.error("[draft] another account's draft could not be set aside: not saving");
    throw new Error('foreign_draft_not_set_aside');
  }
}

export async function savePersistedLoggerState(
  draft: LoggerDraft,
  open: boolean,
): Promise<void> {
  const owner = loggerDraftOwner();
  if (owner === null && loggerDraftOwnerInjected()) return;
  await setAsideForeignDraft(owner);
  try {
    const payload: PersistedLoggerState = {
      draft,
      open,
      inProgress: hasCommittedWork(draft),
      lastWrittenAt: new Date().toISOString(),
    };
    if (owner !== null) payload.owner = owner;
    localStorage.setItem(liveKey(), JSON.stringify(payload));
  } catch {
    // Best-effort; storage failures shouldn't break the logger.
  }
}

export async function clearPersistedLoggerState(): Promise<void> {
  try {
    const raw = localStorage.getItem(liveKey());
    if (raw === null) return;
    const other = blobOwner(raw);
    if (other !== undefined && other !== loggerDraftOwner()) return;
    localStorage.removeItem(liveKey());
  } catch {
    // Best-effort.
  }
}

/** The localStorage twin of setAsideLiveDraft: the live key goes only once
 *  its copy reads back the same. LG-97: web-rnw has no recovery surface, so
 *  the prune here is the only thing that ever deletes what it set aside. */
export async function setAsideLiveDraft(): Promise<boolean> {
  try {
    const raw = localStorage.getItem(liveKey());
    if (raw === null) return true;
    const key = `${quarantinePrefix()}${new Date().toISOString()}`;
    localStorage.setItem(key, raw);
    if (localStorage.getItem(key) !== raw) return false;
    reapQuarantineSlots();
    localStorage.removeItem(liveKey());
    return true;
  } catch (err) {
    console.warn('[draft] live draft not set aside', err);
    return false;
  }
}

/** LG-97, the localStorage half. Same decision function as the AsyncStorage
 *  twin — ⛔ the whole point is that there is ONE answer to "which slots go",
 *  the way `parsePersistedBlob` is one answer to "is this blob sound". */
function reapQuarantineSlots(): void {
  try {
    const prefix = quarantinePrefix();
    const slots: QuarantineSlot[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key === null || !key.startsWith(prefix)) continue;
      const blob = localStorage.getItem(key);
      if (blob === null) continue;
      const state = parsePersistedBlob(blob);
      if (state === null) continue;
      slots.push({
        key,
        quarantinedAt: key.slice(prefix.length),
        hasCommittedWork: hasCommittedWork(state.draft),
      });
    }
    // ⛔ Collect first, remove after: removing inside the loop renumbers
    // localStorage under the index we are walking, and every other key shifts
    // down by one. That is how a "tidy-up" skips half of what it meant to
    // delete, and it would do it silently.
    for (const key of quarantineSlotsToReap(slots, new Date())) {
      localStorage.removeItem(key);
    }
  } catch {
    // Best-effort.
  }
}
